import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ClipboardList, Lock, Send, Star, Users } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  apiGetSurveys,
  apiGetSurveyResults,
  apiPublishSurvey,
  apiCloseSurvey,
  apiSendSurveyReminder,
  type Survey,
} from "@/lib/api";

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-slate-500/10 text-slate-600 border-slate-500/20",
  active: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  closed: "bg-gray-500/10 text-gray-600 border-gray-500/20",
};

export default function MobileSurveysAdmin() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Survey | null>(null);

  const { data: surveys = [] } = useQuery({
    queryKey: ["mobile-surveys-admin"],
    queryFn: apiGetSurveys,
    staleTime: 60_000,
  });

  const { data: results } = useQuery({
    queryKey: ["mobile-survey-results", selected?.id],
    queryFn: () => apiGetSurveyResults(selected!.id),
    enabled: !!selected,
  });

  const publishMutation = useMutation({
    mutationFn: (id: number) => apiPublishSurvey(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mobile-surveys-admin"] });
      toast.success(t("surveys.published"));
      setSelected(null);
    },
  });

  const closeMutation = useMutation({
    mutationFn: (id: number) => apiCloseSurvey(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mobile-surveys-admin"] });
      toast.success(t("surveys.closed"));
      setSelected(null);
    },
  });

  const remindMutation = useMutation({
    mutationFn: (id: number) => apiSendSurveyReminder(id),
    onSuccess: (data) => {
      toast.success(`${t("surveys.reminderSent")} · ${data.reminded} ${t("surveys.employeesReminded")}`);
    },
  });

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto pb-4">
        {surveys.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
            <ClipboardList className="h-10 w-10 opacity-30" />
            <p className="text-sm">{t("surveys.noSurveys")}</p>
          </div>
        ) : (
          <div className="bg-card border border-border/60 mx-4 mt-4 rounded-xl overflow-hidden">
            {surveys.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSelected(s)}
                className="w-full flex items-center gap-3 px-4 py-3 border-b border-border/30 last:border-0 hover:bg-muted/40 active:bg-muted/60 transition-colors text-left"
              >
                <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white shrink-0">
                  <ClipboardList className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-foreground truncate">{s.title}</p>
                    {s.isAnonymous && <Lock className="h-3 w-3 text-muted-foreground shrink-0" />}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {s.questionCount} {t("surveys.questions")} · {s.responseCount} {t("surveys.responses")}
                  </p>
                </div>
                <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_COLORS[s.status] ?? ""}`}>
                  {s.status}
                </Badge>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Results sheet */}
      <Sheet open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <SheetContent side="bottom" className="rounded-t-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <SheetHeader className="text-left">
                <SheetTitle>{selected.title}</SheetTitle>
              </SheetHeader>

              {selected.description && (
                <p className="text-sm text-muted-foreground mt-1">{selected.description}</p>
              )}

              <div className="flex items-center gap-2 mt-3">
                <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[selected.status] ?? ""}`}>
                  {selected.status}
                </Badge>
                {results && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {results.responseCount} / {results.totalEmployees}
                  </span>
                )}
              </div>

              {/* Results */}
              <div className="mt-4 space-y-5">
                {results?.results.map((q, idx) => (
                  <div key={q.questionId} className="space-y-2">
                    <p className="text-sm font-medium leading-snug">
                      {idx + 1}. {q.questionText}
                    </p>
                    {(q.questionType === "single_choice" || q.questionType === "multiple_choice") && (
                      <div className="space-y-1.5">
                        {q.optionCounts?.map((o) => {
                          const total = q.totalResponses || 1;
                          const pct = Math.round((o.count / total) * 100);
                          return (
                            <div key={o.optionId}>
                              <div className="flex justify-between text-xs mb-0.5">
                                <span className="text-foreground/80">{o.optionText}</span>
                                <span className="text-muted-foreground">{o.count} ({pct}%)</span>
                              </div>
                              <div className="w-full bg-muted rounded-full h-1.5">
                                <div className="bg-violet-500 rounded-full h-1.5" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {q.questionType === "rating" && (
                      <div className="flex items-center gap-2">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <Star
                              key={n}
                              className={`h-4 w-4 ${n <= Math.round(q.avgRating ?? 0) ? "text-amber-400 fill-amber-400" : "text-muted-foreground/20"}`}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {(q.avgRating ?? 0).toFixed(1)} · {q.ratingCount ?? 0} {t("surveys.responses")}
                        </span>
                      </div>
                    )}
                    {q.questionType === "text" && (
                      <p className="text-xs text-muted-foreground">
                        {q.textAnswers?.length ?? 0} {t("surveys.responses")}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="mt-5 space-y-2">
                {selected.status === "draft" && (
                  <Button
                    className="w-full h-11"
                    disabled={publishMutation.isPending}
                    onClick={() => publishMutation.mutate(selected.id)}
                  >
                    {t("surveys.published").replace("!", "")}
                  </Button>
                )}
                {selected.status === "active" && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="flex-1 h-11 gap-1.5"
                      disabled={remindMutation.isPending}
                      onClick={() => remindMutation.mutate(selected.id)}
                    >
                      <Send className="h-4 w-4" />
                      {t("surveys.remind")}
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 h-11 text-destructive border-destructive/40 hover:bg-destructive/10"
                      disabled={closeMutation.isPending}
                      onClick={() => closeMutation.mutate(selected.id)}
                    >
                      {t("surveys.closed")}
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
